import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

import { LeftPanel } from "./components/LeftPanel";
import { RightPanel } from "./components/RightPanel";
import { Canvas2DPreview } from "./components/Canvas2DPreview";
import { Viewer, type ViewerHandle, type ViewerStatus } from "./three/Viewer";

import { useEditorState } from "./editor/useEditorState";
import {
  composeTextures,
  createTextureCanvas,
  measureText,
} from "./editor/textureComposer";
import { generateTemplateCanvas } from "./editor/templateGenerator";
import { ImageRegistry } from "./utils/imageRegistry";
import {
  downloadArrayBufferAsGLB,
  downloadCanvasPNG,
} from "./utils/download";
import { makeId } from "./utils/id";
import { exportGLB } from "./three/glbExport";

import type { ArtworkLayer, TextStyle } from "./types";

export default function App() {
  const editor = useEditorState("male");
  const { state, config, selectedLayer } = editor;

  // Two texture canvases owned for the lifetime of the app: one for the
  // base color map (albedo) and one for the emissive map.
  const textureCanvas = useMemo(
    () => createTextureCanvas(config.textureSize),
    // Recreate only if texture size changes between configs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.textureSize],
  );
  const emissiveCanvas = useMemo(
    () => createTextureCanvas(config.textureSize),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.textureSize],
  );

  // Image registry lives for the lifetime of the app.
  const registryRef = useRef<ImageRegistry>(new ImageRegistry());
  useEffect(() => {
    const reg = registryRef.current;
    return () => reg.clear();
  }, []);

  const viewerRef = useRef<ViewerHandle>(null);
  const [viewerStatus, setViewerStatus] = useState<ViewerStatus>({
    kind: "idle",
  });
  const [previewVersion, setPreviewVersion] = useState(0);
  const [exportBusy, setExportBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [debugOverlay, setDebugOverlay] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Recompose base + emissive textures whenever any relevant state changes.
  useEffect(() => {
    composeTextures(textureCanvas, emissiveCanvas, {
      config,
      baseFill: state.baseFill,
      layers: state.layers,
      images: registryRef.current.map(),
      debugOverlay,
    });
    viewerRef.current?.markTextureDirty();
    setPreviewVersion((v) => v + 1);
  }, [
    textureCanvas,
    emissiveCanvas,
    config,
    state.baseFill,
    state.layers,
    debugOverlay,
  ]);

  // --- File upload -> new layers ---
  const onFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const id = makeId();
        try {
          const { imageSrc, naturalWidth, naturalHeight } =
            await registryRef.current.loadFromFile(id, file);

          // Spawn new layer at the calibrated centre of the active side's
          // box. That centre is the shirt-space (0, 0) origin for the
          // sliders, which is the user-visible dead-centre on the 3D
          // model. The artwork can then be dragged anywhere on the texture
          // from there.
          const box =
            state.selectedSide === "front" ? config.frontBox : config.backBox;
          const cx = box.x + box.width / 2;
          const cy = box.y + box.height / 2;

          // Fixed default scale. Users fine-tune with the slider which
          // supports negative values for horizontal mirroring.
          const initialScale = 0.33;

          const layer: ArtworkLayer = {
            id,
            name: file.name.replace(/\.[^/.]+$/, "").slice(0, 48) || "Layer",
            kind: "image",
            imageSrc,
            side: state.selectedSide,
            x: Math.round(cx),
            y: Math.round(cy),
            scale: initialScale,
            rotation: 0,
            opacity: 1,
            visible: true,
            naturalWidth,
            naturalHeight,
            type: "mix",
            emissiveIntensity: 1,
          };
          editor.addLayer(layer);
        } catch (err) {
          console.error(err);
          setToast(
            `Failed to load image "${file.name}". Please try a different file.`,
          );
        }
      }
    },
    [config, editor, state.selectedSide],
  );

  // --- Add a text layer ---
  const onAddText = useCallback(() => {
    const style: TextStyle = {
      text: "Your text",
      fontFamily: "Impact, 'Arial Black', sans-serif",
      fontSize: 128,
      bold: false,
      italic: false,
      color: { kind: "solid", color: "#ffffff" },
      letterSpacing: 0,
      letterTilt: 0,
      letterTiltMode: "uniform",
      lineHeight: 1.2,
    };
    const { width, height } = measureText(style);
    const box =
      state.selectedSide === "front" ? config.frontBox : config.backBox;
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;
    const layer: ArtworkLayer = {
      id: makeId(),
      name: style.text,
      kind: "text",
      text: style,
      side: state.selectedSide,
      x: Math.round(cx),
      y: Math.round(cy),
      // Text sizes are expressed via fontSize; scale = 1 means "as typed".
      scale: 1,
      rotation: 0,
      opacity: 1,
      visible: true,
      naturalWidth: width,
      naturalHeight: height,
      type: "mix",
      emissiveIntensity: 1,
    };
    editor.addLayer(layer);
  }, [config, editor, state.selectedSide]);

  // --- Layer removal also frees the object URL. ---
  const onRemoveLayer = useCallback(
    (id: string) => {
      registryRef.current.release(id);
      editor.removeLayer(id);
    },
    [editor],
  );

  // --- Duplicate a layer to the opposite side ---
  //
  // Clones the source layer with a new id, flips `side`, and re-centres
  // the position on the destination side's box so the copy actually
  // lands on that side instead of sitting at the source side's coords.
  // For image layers we share the already-loaded HTMLImageElement via
  // the ImageRegistry so we don't re-decode.
  const onDuplicateLayerToOtherSide = useCallback(
    (id: string) => {
      const src = state.layers.find((l) => l.id === id);
      if (!src) return;
      const otherSide = src.side === "front" ? "back" : "front";
      const box = otherSide === "front" ? config.frontBox : config.backBox;
      const cx = Math.round(box.x + box.width / 2);
      const cy = Math.round(box.y + box.height / 2);
      const newId = makeId();
      const copy: ArtworkLayer = {
        ...src,
        id: newId,
        name: `${src.name} (${otherSide})`,
        side: otherSide,
        x: cx,
        y: cy,
        // Deep-clone the nested TextStyle (including its Fill) so the
        // duplicate is independent from the source.
        text: src.text
          ? {
              ...src.text,
              color:
                typeof src.text.color === "string"
                  ? src.text.color
                  : src.text.color.kind === "solid"
                    ? { kind: "solid", color: src.text.color.color }
                    : {
                        kind: "gradient",
                        angle: src.text.color.angle,
                        stops: src.text.color.stops.map((s) => ({ ...s })),
                        ...(typeof src.text.color.spread === "number"
                          ? { spread: src.text.color.spread }
                          : {}),
                        ...(typeof src.text.color.offset === "number"
                          ? { offset: src.text.color.offset }
                          : {}),
                      },
            }
          : undefined,
      };
      if (src.kind === "image") registryRef.current.shareTo(src.id, newId);
      editor.addLayer(copy);
      // Follow the duplicated layer into its new side so the 2D
      // preview (which only hit-tests the currently selected side) can
      // actually select and manipulate the fresh copy.
      editor.setSelectedSide(otherSide);
    },
    [config, editor, state.layers],
  );

  const onReset = useCallback(() => {
    // Clean up URLs for current layers.
    for (const l of state.layers) registryRef.current.release(l.id);
    editor.resetDesign();
  }, [editor, state.layers]);

  // --- Downloads ---
  const onDownloadPNG = useCallback(() => {
    downloadCanvasPNG(
      textureCanvas,
      `${config.id}_texture_${Date.now()}.png`,
    );
  }, [textureCanvas, config.id]);

  const onDownloadFrontTemplate = useCallback(() => {
    const c = generateTemplateCanvas(config, "front");
    downloadCanvasPNG(c, `${config.id}_front_template.png`);
  }, [config]);

  const onDownloadBackTemplate = useCallback(() => {
    const c = generateTemplateCanvas(config, "back");
    downloadCanvasPNG(c, `${config.id}_back_template.png`);
  }, [config]);

  // --- GLB export ---
  const onExportGLB = useCallback(async () => {
    const v = viewerRef.current;
    if (!v) return;
    const root = v.getRoot();
    if (!root) {
      setToast("Model not loaded yet.");
      return;
    }
    setExportBusy(true);
    try {
      // Ensure the texture on the material is fully up to date.
      v.markTextureDirty();
      // Wait a frame to let three.js upload the texture before parse reads it.
      await new Promise((r) => requestAnimationFrame(() => r(null)));

      // Export the scene's top-level children (e.g. Armature.001) rather
      // than the GLTFLoader wrapper Group. This preserves the original
      // DCL wearable topology: scene → Armature → bones + mesh.
      const topLevel = [...root.children];
      if (topLevel.length === 0) {
        throw new Error("Loaded model has no top-level nodes to export.");
      }

      const buffer = await exportGLB(topLevel, { binary: true });
      downloadArrayBufferAsGLB(
        buffer,
        `${config.id}_${Date.now()}.glb`,
      );
    } catch (err) {
      console.error(err);
      setToast(
        err instanceof Error ? `GLB export failed: ${err.message}` : "GLB export failed.",
      );
    } finally {
      setExportBusy(false);
    }
  }, [config.id]);

  const exportDisabled =
    viewerStatus.kind !== "ready" && viewerStatus.kind !== "missing-material";

  const onSnapView = useCallback(
    (side: "front" | "back" | "left" | "right") => {
      viewerRef.current?.snapToView(side);
    },
    [],
  );

  // --- Drag & drop images directly onto the centre preview pane. ---
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // dragenter/dragleave fire for every descendant, so we ref-count them
  // to avoid the overlay flickering as the pointer crosses child elements.
  const dragDepthRef = useRef(0);

  const dragHasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes("Files");

  const onPaneDragEnter = useCallback((e: React.DragEvent) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingOver(true);
  }, []);

  const onPaneDragOver = useCallback((e: React.DragEvent) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }, []);

  const onPaneDragLeave = useCallback((e: React.DragEvent) => {
    if (!dragHasFiles(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingOver(false);
  }, []);

  const onPaneDrop = useCallback(
    (e: React.DragEvent) => {
      if (!dragHasFiles(e)) return;
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDraggingOver(false);
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith("image/"),
      );
      if (files.length === 0) {
        setToast("Please drop image files (PNG, JPG, etc).");
        return;
      }
      void onFiles(files);
    },
    [onFiles],
  );

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" />
          <span className="brand-name">DCL Wearable Generator</span>
          <span className="brand-tag">v1 · long sleeve</span>
        </div>
        <div className="status">
          {viewerStatus.kind === "loading" && "Loading model…"}
          {viewerStatus.kind === "ready" && "Ready"}
          {viewerStatus.kind === "missing-material" &&
            `Material "${viewerStatus.materialName}" not found`}
          {viewerStatus.kind === "error" && `Error: ${viewerStatus.message}`}
        </div>
      </header>

      <div className="layout">
        <LeftPanel
          gender={editor.gender}
          onGender={editor.setGender}
          wearableLabel={config.label}
          selectedSide={state.selectedSide}
          onSelectedSide={editor.setSelectedSide}
          onReset={onReset}
          onDownloadFrontTemplate={onDownloadFrontTemplate}
          onDownloadBackTemplate={onDownloadBackTemplate}
          onDownloadPNG={onDownloadPNG}
          onExportGLB={onExportGLB}
          onSnapView={onSnapView}
          debugOverlay={debugOverlay}
          onDebugOverlay={setDebugOverlay}
          exportBusy={exportBusy}
          exportDisabled={exportDisabled}
        />

        <main
          className={`panel center${isDraggingOver ? " dragging-over" : ""}`}
          onDragEnter={onPaneDragEnter}
          onDragOver={onPaneDragOver}
          onDragLeave={onPaneDragLeave}
          onDrop={onPaneDrop}
        >
          <div className="center-viewer">
            <Viewer
              ref={viewerRef}
              config={config}
              textureCanvas={textureCanvas}
              emissiveCanvas={emissiveCanvas}
              onStatus={setViewerStatus}
            />
            {isDraggingOver && (
              <div className="center-drop-overlay" aria-hidden>
                <div className="center-drop-card">
                  <div className="center-drop-title">Drop image to add layer</div>
                  <div className="center-drop-sub">
                    PNG or JPG — goes onto the {state.selectedSide} side
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="center-editor">
            <h3 className="group-title">2D Editor</h3>
            <Canvas2DPreview
              config={config}
              selectedSide={state.selectedSide}
              textureCanvas={textureCanvas}
              version={previewVersion}
              selectedLayerId={state.selectedLayerId}
              layers={state.layers}
              onSelectLayer={editor.selectLayer}
              onMoveLayer={(id, x, y) => editor.updateLayer(id, { x, y })}
              onRotateLayer={(id, rotation) =>
                editor.updateLayer(id, { rotation })
              }
              onScaleLayer={(id, scale) => editor.updateLayer(id, { scale })}
            />
            <p className="hint">
              Drag the selected layer to move. Click on its bounding box to
              scale, or just outside it to rotate. Use the sliders on the
              right for precise control.
            </p>
          </div>
        </main>

        <RightPanel
          config={config}
          baseFill={state.baseFill}
          onBaseFill={editor.setBaseFill}
          onFiles={onFiles}
          onAddText={onAddText}
          layers={state.layers}
          selectedLayerId={state.selectedLayerId}
          onSelectLayer={editor.selectLayer}
          onToggleVisible={(id) => {
            const l = state.layers.find((x) => x.id === id);
            if (l) editor.updateLayer(id, { visible: !l.visible });
          }}
          onReorderLayer={editor.reorderLayer}
          onRemoveLayer={onRemoveLayer}
          onDuplicateLayer={onDuplicateLayerToOtherSide}
          selectedLayer={selectedLayer}
          onUpdateLayer={editor.updateLayer}
        />
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
